import { forwardRef, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react'
import type { To } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from './Button'
import './back-link.css'

/** Default is a secondary pill on the light canvas; inverse is the ink button for dark photography. */
export type BackLinkTone = 'default' | 'inverse'

export type BackLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children'> & {
  /** The app route it goes back to. */
  to: To
  /** The destination's name, such as Events or the event's name; it is read as "Back to Events". Leave it out only
   *  when the destination can't be named, such as a return path another page passed in: it then says Back. */
  children?: ReactNode
  tone?: BackLinkTone
  /** Keeps people on the page while a task that can't be left saves, such as creating an event. It stays focusable. */
  disabled?: boolean
  /** Replace the current history entry instead of adding one. */
  replace?: boolean
}

/** The way back to the page above this one, named for it. It sits top left, above the page heading. */
export const BackLink = forwardRef<HTMLAnchorElement, BackLinkProps>(function BackLink(
  { to, children, tone = 'default', disabled = false, className, onClick, ...props },
  ref,
) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (disabled) {
      event.preventDefault()
      return
    }
    onClick?.(event)
  }
  return (
    <ButtonLink
      {...props}
      ref={ref}
      to={to}
      variant={tone === 'inverse' ? 'inverse' : 'secondary'}
      className={cn('play-back-link', className)}
      aria-disabled={disabled || undefined}
      data-disabled={disabled ? '' : undefined}
      onClick={handleClick}
    >
      <ArrowLeft aria-hidden="true" />
      <span className="play-back-link__label">
        {children ? <>Back to {children}</> : 'Back'}
      </span>
    </ButtonLink>
  )
})
